import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Link,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ClaimStatus, EhrClaim, ProviderWithAperoId } from '../../interfaces/billing';
import { BillingService } from '../../services';
import { StyledTheme } from '../../styles/styleTheme';
import { formatDateToDayAndDate } from '../../util/dateFormat';
import {
  getAperoUrl,
  getDrChronoAppointmentUrl,
  getDrChronoPatientUrl,
  getEnaraUrl,
} from '../../util/url';
import { ClaimLogs } from './ClaimLogs';

const SingleClaims: React.FC = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const history = useHistory();

  const [claim, setClaim] = useState<EhrClaim | null>(null);
  const [loading, setLoading] = useState(true);
  const [forceEhrFetch, setForceEhrFetch] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [message, setMessage] = useState('');

  // Provider override state
  const [isEditingProvider, setIsEditingProvider] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ProviderWithAperoId | null>(null);
  const [providers, setProviders] = useState<ProviderWithAperoId[]>([]);
  const [isSavingOverride, setIsSavingOverride] = useState(false);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);

  useEffect(() => {
    const fetchClaim = async () => {
      try {
        const response = await new BillingService().getClaim(Number(claimId));
        if (response?.claim) {
          setClaim(response.claim);
        } else {
          history.push('/claims');
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchClaim();
  }, [claimId, history]);

  // Fetch providers when editing starts
  useEffect(() => {
    const fetchProviders = async () => {
      if (!isEditingProvider || !claim?.clinicId) return;

      setIsLoadingProviders(true);
      try {
        const response = await new BillingService().getProvidersByClinic(claim.clinicId);
        if (response?.providers) {
          setProviders(response.providers);
          // Find and select the current override provider if exists
          if (claim.overrides?.aperoProviderId) {
            const currentProvider = response.providers.find(
              (p) => p.apero_provider_id === claim.overrides?.aperoProviderId,
            );
            setSelectedProvider(currentProvider || null);
          }
        }
      } catch (err) {
        console.error(err);
        setMessage('Error loading providers');
      } finally {
        setIsLoadingProviders(false);
      }
    };
    fetchProviders();
  }, [isEditingProvider, claim?.clinicId, claim?.overrides?.aperoProviderId]);

  const handleToggleLogs = () => setShowLogsDialog(!showLogsDialog);
  const handleForceEhrFetch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setForceEhrFetch(event.target.checked);
  const handleBack = () => history.push('/claims');

  const handleSaveProviderOverride = async () => {
    if (!claim) return;

    setIsSavingOverride(true);
    try {
      const overrides = selectedProvider?.apero_provider_id
        ? { aperoProviderId: selectedProvider.apero_provider_id }
        : { aperoProviderId: null as unknown as string }; // Setting null removes the override

      const response = await new BillingService().updateClaimOverrides(claim.id, overrides);

      if (response?.success) {
        setClaim(response.claim);
        setMessage('Provider override saved successfully');
        setIsEditingProvider(false);
      } else {
        setMessage(response?.message || 'Error saving provider override');
      }
    } catch (err) {
      setMessage('Error saving provider override');
    } finally {
      setIsSavingOverride(false);
    }
  };

  const handleCancelEditProvider = () => {
    setSelectedProvider(null);
    setIsEditingProvider(false);
  };

  const handleResync = async () => {
    if (!claim) {
      setMessage('No claim selected for resync');
      return;
    }

    setIsResyncing(true);
    try {
      const response = await new BillingService().reSyncEhrClaim(claim.id, {
        forceEhrFetching: forceEhrFetch,
      });
      if (!response) {
        setMessage('Unexpected error resyncing claim');
      } else {
        setMessage(
          response.success
            ? 'Claim resynced successfully'
            : response.message || 'Error resyncing claim',
        );
      }
    } catch (err) {
      setMessage('Error resyncing claim');
    } finally {
      setIsResyncing(false);
    }
  };

  if (loading) {
    return (
      <div className='loading-overlay'>
        <div className='loading-spinner'></div>
      </div>
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <Button onClick={handleBack} color='primary' variant='contained'>
          <ArrowBackIcon /> Back
        </Button>
      </Box>
      <Box p={2}>
        <Box mb={2} textAlign='center'>
          <Typography variant='h6'>{formatDateToDayAndDate(claim?.serviceDate ?? '')}</Typography>
        </Box>
        <Box mb={2}>
          <Typography sx={{ fontWeight: 'bolder' }}>Claim ID:</Typography>
          <Typography>{claim?.id ?? 'N/A'}</Typography>
        </Box>
        <Grid container spacing={2} mb={4}>
          {[
            {
              label: 'DrChrono Patient',
              value: claim?.ehrAppointmentData?.appointment?.patient
                ? `${claim?.corePatient?.firstName ?? 'N/A'} ${
                    claim?.corePatient?.lastName ?? 'N/A'
                  }`
                : 'Name not available',
              link: claim?.ehrAppointmentData?.appointment?.patient
                ? getDrChronoPatientUrl(claim?.ehrAppointmentData?.appointment?.patient)
                : '',
            },
            {
              label: 'DrChrono Appointment',
              value: claim?.ehrAppointmentData?.appointment?.id ?? 'N/A',
              link: claim?.ehrAppointmentData?.appointment?.id
                ? getDrChronoAppointmentUrl(claim?.ehrAppointmentData?.appointment?.id)
                : '',
            },
            {
              label: 'Core appointment',
              value: claim?.coreAppointmentData?.id ?? 'N/A',
              link: claim?.coreAppointmentData?.id
                ? getEnaraUrl(`/activeadmin/schedulings/${claim?.coreAppointmentData?.id}`)
                : '',
            },
            {
              label: 'Provider',
              value: claim?.coreProvider?.firstName
                ? `${claim?.coreProvider?.firstName ?? 'N/A'} ${
                    claim?.coreProvider?.lastName ?? 'N/A'
                  }`
                : 'N/A',
              link: claim?.coreProvider?.id
                ? getEnaraUrl(`/activeadmin/users/${claim?.coreProvider?.id}`)
                : '',
            },
            {
              label: 'Apero invoice',
              value: claim?.aperoInvoiceData?.invoice?.id ?? 'N/A',
              link: claim?.aperoInvoiceData?.invoice?.id
                ? getAperoUrl(`/invoices/${claim?.aperoInvoiceData?.invoice?.id}`)
                : '',
            },
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Typography sx={{ fontWeight: 'bolder' }}>{item.label}:</Typography>
              <Typography>
                {item.link ? (
                  <Link href={item.link} target='_blank'>
                    {item.value}
                  </Link>
                ) : (
                  item.value
                )}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Provider Override Section */}
        <Box
          mt={3}
          mb={3}
          p={2}
          sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
          <Typography sx={{ fontWeight: 'bolder', mb: 1 }}>
            Apero Provider Override
            <Tooltip title='Override the Apero provider ID used when syncing this claim. Leave empty to use the default provider.'>
              <IconButton size='small' sx={{ ml: 0.5 }}>
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Typography>

          {isEditingProvider ? (
            <Box display='flex' alignItems='center' gap={1}>
              <Autocomplete
                size='small'
                fullWidth
                loading={isLoadingProviders}
                options={providers.filter((p) => p.apero_provider_id)} // Only show providers with Apero ID
                value={selectedProvider}
                onChange={(_, newValue) => setSelectedProvider(newValue)}
                getOptionLabel={(option) =>
                  `${option.first_name} ${option.last_name} (${option.apero_provider_id || 'No Apero ID'})`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Select a provider...'
                    disabled={isSavingOverride}
                  />
                )}
                disabled={isSavingOverride}
              />
              <Button
                variant='contained'
                color='primary'
                size='small'
                onClick={handleSaveProviderOverride}
                disabled={isSavingOverride || isLoadingProviders}
                startIcon={<SaveIcon />}>
                Save
              </Button>
              <Button
                variant='outlined'
                size='small'
                onClick={handleCancelEditProvider}
                disabled={isSavingOverride}>
                Cancel
              </Button>
            </Box>
          ) : (
            <Box display='flex' alignItems='center' gap={1}>
              <Typography>
                {claim?.overrides?.aperoProviderId ? (
                  <>
                    <Link
                      href={getAperoUrl(`/users/${claim.overrides.aperoProviderId}`)}
                      target='_blank'>
                      {claim.overrides.aperoProviderId}
                    </Link>
                    {/* Show provider name if available */}
                    {providers.find((p) => p.apero_provider_id === claim.overrides?.aperoProviderId)
                      ? ` (${providers.find((p) => p.apero_provider_id === claim.overrides?.aperoProviderId)?.first_name} ${providers.find((p) => p.apero_provider_id === claim.overrides?.aperoProviderId)?.last_name})`
                      : ''}
                  </>
                ) : (
                  <span style={{ color: '#999' }}>Not set (using default)</span>
                )}
              </Typography>
              <IconButton
                size='small'
                onClick={() => setIsEditingProvider(true)}
                sx={{ color: StyledTheme.tealDark }}>
                <EditIcon fontSize='small' />
              </IconButton>
            </Box>
          )}
        </Box>

        <Grid container justifyContent='space-between' alignItems='center' mt={4}>
          <Grid item>
            <Button
              size='large'
              style={{
                background: StyledTheme.grayDark,
                color: StyledTheme.white,
                textTransform: 'capitalize',
              }}
              onClick={handleToggleLogs}>
              <FormatAlignJustifyIcon /> View Logs
            </Button>
          </Grid>
          {claim?.status !== ClaimStatus.Processed && (
            <Grid item>
              <Box display='flex' alignItems='center'>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        style={{ color: StyledTheme.tealDark }}
                        value={forceEhrFetch}
                        onChange={handleForceEhrFetch}
                        checked={forceEhrFetch}
                      />
                    }
                    label='Force EHR appointment download?'
                  />
                </FormGroup>
                <Button
                  size='large'
                  color='primary'
                  variant='contained'
                  disabled={isResyncing}
                  onClick={handleResync}>
                  Attempt Resync
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      <Dialog open={showLogsDialog} onClose={handleToggleLogs} maxWidth='md' fullWidth>
        <DialogTitle>
          Claim logs
          <IconButton
            aria-label='close'
            onClick={handleToggleLogs}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <ClaimLogs logs={claim?.ehrClaimLogs ?? []} />
        </DialogContent>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!message}
        autoHideDuration={10000}
        onClose={() => setMessage('')}
        message={message}
        ContentProps={{
          style: { marginTop: '50px' },
        }}
      />
    </Box>
  );
};

export default SingleClaims;
